import React, { useCallback, useEffect, useRef, useState } from "react";
import { Drawer, Modal } from "antd";
import Icon from "@mdi/react";
import { mdiAirplane, mdiChevronDown, mdiTuneVariant } from "@mdi/js";
import DestinationPicker, { DestinationSelection } from "./DestinationPicker";
import ScrollCarousel from "./ScrollCarousel";
import SheetHeaderClose from "./SheetHeaderClose";
import { PinIcon } from "../search-icons";
import type { Country } from "../../../../interfaces/country.interface";
import type { Tag } from "../../../../interfaces/tag.interface";
import type { ListingFilterState } from "../../../../utils/listingSearchParams";

interface Props {
  isMobile: boolean;
  filters: ListingFilterState;
  countries: Country[];
  tags: Tag[];
  filterCount: number;
  onOpenFilters: () => void;
  onChange: (next: ListingFilterState) => void;
}

export default function ListingFilterBar({
  isMobile,
  filters,
  countries,
  tags,
  filterCount,
  onOpenFilters,
  onChange,
}: Props) {
  const [destinationOpen, setDestinationOpen] = useState(false);

  const scrollerRef = useRef<HTMLDivElement>(null);
  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);

  const updateFades = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setFadeLeft(el.scrollLeft > 2);
    setFadeRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    updateFades();
    window.addEventListener("resize", updateFades);
    return () => window.removeEventListener("resize", updateFades);
  }, [updateFades, tags]);

  const guaranteed = filters.guarantees.includes("guarantee");
  const destinationCount = filters.keyword || filters.locationId ? 1 : 0;

  const toggleGuaranteed = () =>
    onChange({ ...filters, guarantees: guaranteed ? [] : ["guarantee"] });

  const toggleTag = (id: string) =>
    onChange({
      ...filters,
      tags: filters.tags.includes(id) ? filters.tags.filter((tag) => tag !== id) : [...filters.tags, id],
    });

  const applyDestination = (selection: DestinationSelection) => {
    onChange({ ...filters, keyword: selection.keyword, locationId: selection.locationId });
    setDestinationOpen(false);
  };

  const picker = (
    <DestinationPicker
      countries={countries}
      selected={{ keyword: filters.keyword, locationId: filters.locationId }}
      isMobile={isMobile}
      onApply={applyDestination}
    />
  );

  const destination = (
    <React.Fragment key="destination">
      <button
        type="button"
        className={`v2-chip ${destinationCount ? "v2-chip--red is-active" : ""}`}
        onClick={() => setDestinationOpen(true)}
      >
        <PinIcon size={16} />
        Destination{destinationCount ? ` (${destinationCount})` : ""}
        <Icon path={mdiChevronDown} size="18px" />
      </button>

      {isMobile ? (
        <Drawer
          open={destinationOpen}
          onClose={() => setDestinationOpen(false)}
          placement="bottom"
          height="90%"
          title="Destination"
          closable={false}
          extra={<SheetHeaderClose onClose={() => setDestinationOpen(false)} />}
          rootClassName="v2-sheet v2-sheet--destinations"
        >
          {picker}
        </Drawer>
      ) : (
        <Modal
          open={destinationOpen}
          onCancel={() => setDestinationOpen(false)}
          title="Destination"
          width={900}
          centered
          rootClassName="v2-filters-panel v2-destinations-modal"
          footer={null}
        >
          {picker}
        </Modal>
      )}
    </React.Fragment>
  );

  const guaranteedChip = (
    <button
      key="guaranteed"
      type="button"
      className={`v2-chip v2-chip--green ${guaranteed ? "is-active" : ""}`}
      onClick={toggleGuaranteed}
    >
      <Icon path={mdiAirplane} size="16px" />
      Guaranteed Departure
    </button>
  );

  const tagsRow = (
    <React.Fragment key="tags">
      {tags.map((tag) => (
        <button
          key={tag.id}
          type="button"
          title={tag.description}
          className={`v2-chip v2-chip--orange ${filters.tags.includes(tag.id) ? "is-active" : ""}`}
          onClick={() => toggleTag(tag.id)}
        >
          {tag.name}
        </button>
      ))}
    </React.Fragment>
  );

  const filtersButton = (
    <button type="button" className="v2-chip v2-chip--filters" onClick={onOpenFilters}>
      <Icon path={mdiTuneVariant} size="16px" />
      {!isMobile && "Filters"}
      {filterCount > 0 && <span className="v2-chip__badge">{filterCount}</span>}
    </button>
  );

  if (isMobile) {
    return (
      <div
        ref={scrollerRef}
        onScroll={updateFades}
        className={`v2-listing__filterbar ${fadeLeft ? "has-fade-left" : ""} ${fadeRight ? "has-fade-right" : ""}`}
      >
        {filtersButton}
        {[destination, guaranteedChip, tagsRow]}
      </div>
    );
  }

  return (
    <div className="v2-listing__filterbar">
      {filtersButton}
      {destination}
      {guaranteedChip}
      <span className="v2-listing__filterbar-divider" aria-hidden="true" />
      <div className="v2-listing__promos-carousel">
        <ScrollCarousel trackClassName="v2-listing__promos-track" step={240}>
          {tagsRow}
        </ScrollCarousel>
      </div>
    </div>
  );
}
